import React, { useState } from 'react';
import Icon from 'components/AppIcon';


const MediaUploadSection = ({ onFilesChange }) => {
  const [selectedFiles, setSelectedFiles] = useState([]);
  const [processingError, setProcessingError] = useState(null);
  const maxFiles = 5;
  const minFiles = 3;

  // Supported and blocked formats
  const WEB_STANDARD_FORMATS = ['image/jpeg', 'image/jpg', 'image/png', 'image/webp'];
  const HEIC_FORMATS = ['image/heic', 'image/heif'];
  const AVIF_FORMATS = ['image/avif'];
  const RAW_FORMATS = [
    'image/x-adobe-dng',
    'image/x-nikon-nef',
    'image/x-sony-arw',
    'image/x-canon-cr2',
    'image/x-canon-crw',
    'image/x-pentax-pef',
    'image/x-olympus-orf',
    'image/x-panasonic-raw',
    'image/x-fuji-raf'
  ];
  const RAW_EXTENSIONS = ['.dng', '.nef', '.arw', '.cr2', '.crw', '.pef', '.orf', '.raw', '.raf'];

  // Check if file is RAW format
  const isRawFormat = (file) => {
    const fileName = file?.name?.toLowerCase() || '';
    const fileType = file?.type?.toLowerCase() || '';
    
    // Check by MIME type
    if (RAW_FORMATS?.some(format => fileType?.includes(format))) {
      return true;
    }
    
    // Check by extension
    return RAW_EXTENSIONS?.some(ext => fileName?.endsWith(ext));
  };

  // Check if file is HEIC/HEIF
  const isHeicFormat = (file) => {
    const fileName = file?.name?.toLowerCase() || '';
    const fileType = file?.type?.toLowerCase() || '';
    
    return HEIC_FORMATS?.includes(fileType) || 
           fileName?.endsWith('.heic') || 
           fileName?.endsWith('.heif');
  };

  // Check if file is AVIF format (not supported by Supabase Storage)
  const isAvifFormat = (file) => {
    const fileName = file?.name?.toLowerCase() || '';
    const fileType = file?.type?.toLowerCase() || '';
    return AVIF_FORMATS?.includes(fileType) || fileName?.endsWith('.avif');
  };

  // Check if file is web-standard format that can be resized
  const isWebStandardFormat = (file) => {
    return WEB_STANDARD_FORMATS?.includes(file?.type?.toLowerCase());
  };

  // Convert AVIF (or any image) to JPEG using Canvas
  const convertToJpeg = (file) => {
    return new Promise((resolve, reject) => {
      const reader = new FileReader();
      reader.onload = (e) => {
        const img = new Image();
        img.onload = () => {
          try {
            const maxDimension = 1600;
            let width = img.width;
            let height = img.height;
            if (width > maxDimension || height > maxDimension) {
              if (width > height) {
                height = (height / width) * maxDimension;
                width = maxDimension;
              } else {
                width = (width / height) * maxDimension;
                height = maxDimension;
              }
            }
            const canvas = document.createElement('canvas');
            canvas.width = width;
            canvas.height = height;
            const ctx = canvas.getContext('2d');
            ctx.drawImage(img, 0, 0, width, height);
            canvas.toBlob(
              (blob) => {
                if (blob) {
                  const newName = file.name.replace(/\.avif$/i, '.jpg');
                  const convertedFile = new File([blob], newName, {
                    type: 'image/jpeg',
                    lastModified: Date.now()
                  });
                  resolve(convertedFile);
                } else {
                  reject(new Error('Canvas to blob conversion failed'));
                }
              },
              'image/jpeg',
              0.85
            );
          } catch (error) {
            reject(error);
          }
        };
        img.onerror = () => reject(new Error('Image loading failed'));
        img.src = e.target.result;
      };
      reader.onerror = () => reject(new Error('File reading failed'));
      reader.readAsDataURL(file);
    });
  };

  // Resize image using Canvas API
  const resizeImage = (file) => {
    return new Promise((resolve, reject) => {
      const reader = new FileReader();
      
      reader.onload = (e) => {
        const img = new Image();
        
        img.onload = () => {
          try {
            // Calculate new dimensions
            const maxDimension = 1600;
            let width = img.width;
            let height = img.height;
            
            if (width > maxDimension || height > maxDimension) {
              if (width > height) {
                height = (height / width) * maxDimension;
                width = maxDimension;
              } else {
                width = (width / height) * maxDimension;
                height = maxDimension;
              }
            }
            
            // Create canvas and resize
            const canvas = document.createElement('canvas');
            canvas.width = width;
            canvas.height = height;
            
            const ctx = canvas.getContext('2d');
            ctx.drawImage(img, 0, 0, width, height);
            
            // Convert to blob with compression
            canvas.toBlob(
              (blob) => {
                if (blob) {
                  // Create new file with resized blob
                  const resizedFile = new File([blob], file.name, {
                    type: file.type,
                    lastModified: Date.now()
                  });
                  resolve(resizedFile);
                } else {
                  reject(new Error('Canvas to blob conversion failed'));
                }
              },
              file.type,
              0.7 // Quality
            );
          } catch (error) {
            reject(error);
          }
        };
        
        img.onerror = () => {
          reject(new Error('Image loading failed'));
        };
        
        img.src = e.target.result;
      };
      
      reader.onerror = () => {
        reject(new Error('File reading failed'));
      };
      
      reader.readAsDataURL(file);
    });
  };

  // Process files with appropriate handling
  const processFiles = async (files) => {
    const processedFiles = [];
    const errors = [];

    for (const file of files) {
      // Block RAW formats
      if (isRawFormat(file)) {
        errors?.push(`${file?.name}: Formato RAW non supportato. Converti in JPEG o PNG prima di caricare.`);
        continue;
      }

      // Handle HEIC/HEIF - upload as-is (raw binary)
      if (isHeicFormat(file)) {
        processedFiles?.push(file);
        continue;
      }

      // Handle AVIF - convert to JPEG (Supabase Storage doesn't support image/avif)
      if (isAvifFormat(file)) {
        try {
          const convertedFile = await convertToJpeg(file);
          processedFiles?.push(convertedFile);
        } catch (error) {
          console.warn(`AVIF conversion failed for ${file?.name}, skipping:`, error);
          errors?.push(`${file?.name}: Impossibile convertire il file AVIF. Prova a salvarlo come JPEG o PNG.`);
        }
        continue;
      }

      // Handle web-standard formats - resize
      if (isWebStandardFormat(file)) {
        try {
          const resizedFile = await resizeImage(file);
          processedFiles?.push(resizedFile);
        } catch (error) {
          // Fallback: upload original if resize fails
          console.warn(`Resize failed for ${file?.name}, uploading original:`, error);
          processedFiles?.push(file);
        }
        continue;
      }

      // Other image formats - upload as-is
      if (file?.type?.startsWith('image/')) {
        processedFiles?.push(file);
      }
    }

    return { processedFiles, errors };
  };

  const handleFileSelect = async (e) => {
    const files = Array.from(e?.target?.files || []);
    setProcessingError(null);
    
    // Filter only images
    const imageFiles = files?.filter(file => file?.type?.startsWith('image/'));
    
    if (imageFiles?.length === 0) {
      setProcessingError('Seleziona solo file immagine.');
      return;
    }

    // Process files
    const { processedFiles, errors } = await processFiles(imageFiles);

    // Show errors if any
    if (errors?.length > 0) {
      setProcessingError(errors?.join(' '));
    }

    // Add processed files (limit to max)
    if (processedFiles?.length > 0) {
      const newFiles = [...selectedFiles, ...processedFiles]?.slice(0, maxFiles);
      setSelectedFiles(newFiles);
      
      // Notify parent component of file changes
      if (onFilesChange) {
        onFilesChange(newFiles);
      }
    }

    // Reset input
    e.target.value = '';
  };

  const handleRemoveFile = (index) => {
    const updatedFiles = selectedFiles?.filter((_, i) => i !== index);
    setSelectedFiles(updatedFiles);
    
    // Notify parent component of file changes
    if (onFilesChange) {
      onFilesChange(updatedFiles);
    }
  };

  const handleClearAll = () => {
    setSelectedFiles([]);
    setProcessingError(null);
    
    // Notify parent component of file changes
    if (onFilesChange) {
      onFilesChange([]);
    }
  };

  return (
    <div className="bg-white rounded-lg shadow-sm border border-gray-200 p-6">
      <div className="flex items-center gap-3 mb-4">
        <div className="w-10 h-10 bg-green-100 rounded-lg flex items-center justify-center">
          <Icon name="Image" size={20} color="#22c55e" />
        </div>
        <div>
          <h2 className="text-lg font-semibold text-foreground">Foto e Immagini</h2>
          <p className="text-sm text-muted-foreground">
            Aggiungi foto per aiutare i professionisti a capire meglio (opzionale)
          </p>
        </div>
      </div>

      <div className="space-y-4">
        {/* Error Message */}
        {processingError && (
          <div className="bg-red-50 border border-red-200 rounded-lg p-4 flex gap-3">
            <Icon name="AlertCircle" size={20} color="#ef4444" className="flex-shrink-0 mt-0.5" />
            <div className="text-sm text-red-900">
              <p className="font-medium mb-1">Errore</p>
              <p className="text-red-700">{processingError}</p>
            </div>
          </div>
        )}

        {/* Upload Area */}
        <div className="border-2 border-dashed border-gray-300 rounded-lg p-6 text-center hover:border-primary transition-colors">
          <input
            type="file"
            id="photoUpload"
            accept="image/*"
            multiple
            onChange={handleFileSelect}
            className="hidden"
            disabled={selectedFiles?.length >= maxFiles}
          />
          <label
            htmlFor="photoUpload"
            className={`cursor-pointer ${
              selectedFiles?.length >= maxFiles ? 'opacity-50 cursor-not-allowed' : ''
            }`}
          >
            <div className="flex flex-col items-center gap-3">
              <div className="w-16 h-16 bg-gray-100 rounded-full flex items-center justify-center">
                <Icon name="Upload" size={28} color="#6b7280" />
              </div>
              <div>
                <p className="text-sm font-medium text-foreground">
                  Clicca per caricare foto
                </p>
                <p className="text-xs text-muted-foreground mt-1">
                  Opzionale — carica fino a {maxFiles} foto (solo immagini)
                </p>
              </div>
            </div>
          </label>
        </div>

        {/* Selected Files Preview */}
        {selectedFiles?.length > 0 && (
          <div className="space-y-3">
            <div className="flex items-center justify-between">
              <p className="text-sm font-medium text-foreground">
                Foto selezionate ({selectedFiles?.length}/{maxFiles})
              </p>
              <button
                type="button"
                onClick={handleClearAll}
                className="text-xs text-destructive hover:underline"
              >
                Rimuovi tutte
              </button>
            </div>

            <div className="grid grid-cols-2 sm:grid-cols-3 gap-3">
              {selectedFiles?.map((file, index) => (
                <div
                  key={index}
                  className="relative group border border-gray-200 rounded-lg overflow-hidden aspect-square"
                >
                  <img
                    src={URL.createObjectURL(file)}
                    alt={`Preview ${index + 1}`}
                    className="w-full h-full object-cover"
                  />
                  <button
                    type="button"
                    onClick={() => handleRemoveFile(index)}
                    className="absolute top-2 right-2 w-6 h-6 bg-destructive text-white rounded-full flex items-center justify-center opacity-0 group-hover:opacity-100 transition-opacity"
                  >
                    <Icon name="X" size={14} />
                  </button>
                  <div className="absolute bottom-0 left-0 right-0 bg-black/60 text-white text-xs p-2 truncate">
                    {file?.name}
                  </div>
                </div>
              ))}
            </div>
          </div>
        )}

        {/* Info Box */}
        <div className="bg-green-50 border border-green-200 rounded-lg p-4 flex gap-3">
          <Icon name="Info" size={20} color="#22c55e" className="flex-shrink-0 mt-0.5" />
          <div className="text-sm text-green-900">
            <p className="font-medium mb-1">Suggerimento</p>
            <p className="text-green-700">
              Le foto aiutano i professionisti a valutare meglio il lavoro e a fornirti preventivi più accurati. Carica immagini chiare dell'area o del problema.
            </p>
          </div>
        </div>
      </div>
    </div>
  );
};

export default MediaUploadSection;